import { create } from 'zustand';
import type { KeyDatePage } from '@edwin-group/people-graphql';
import type { KeyDate, PaginationInfo } from '../../shared/types';

const getCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

interface KeyDatesListState {
  keyDates: KeyDate[];
  pagination: PaginationInfo;
  loading: boolean;
  currentMonth: Date;
  searchTerm: string;
  sectionType: string | null;
  page: KeyDatePage | null;
}

interface KeyDatesListActions {
  setKeyDates: (keyDates: KeyDate[]) => void;
  appendKeyDates: (keyDates: KeyDate[]) => void;
  setPagination: (pagination: PaginationInfo) => void;
  setLoading: (loading: boolean) => void;
  setCurrentMonth: (month: Date) => void;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  setSearchTerm: (term: string) => void;
  setSectionType: (sectionType: string | null) => void;
  setPage: (page: KeyDatePage | null) => void;
  reset: () => void;
  resetFilters: () => void;
}

const initialState: KeyDatesListState = {
  keyDates: [],
  pagination: {
    page: 1,
    pageCount: 1,
    total: 0,
    pageSize: 50,
  },
  loading: false,
  currentMonth: getCurrentMonth(),
  searchTerm: '',
  sectionType: null,
  page: null,
};

export const useKeyDatesListStore = create<KeyDatesListState & KeyDatesListActions>((set) => ({
  ...initialState,
  setKeyDates: (keyDates) => set({ keyDates }),
  appendKeyDates: (keyDates) => set((state) => ({ keyDates: [...state.keyDates, ...keyDates] })),
  setPagination: (pagination) => set({ pagination }),
  setLoading: (loading) => set({ loading }),
  setCurrentMonth: (currentMonth) => set({ currentMonth }),
  goToPreviousMonth: () =>
    set((state) => ({
      currentMonth: new Date(state.currentMonth.getFullYear(), state.currentMonth.getMonth() - 1, 1),
    })),
  goToNextMonth: () =>
    set((state) => ({
      currentMonth: new Date(state.currentMonth.getFullYear(), state.currentMonth.getMonth() + 1, 1),
    })),
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setSectionType: (sectionType) => set({ sectionType }),
  setPage: (page) => set({ page }),
  reset: () => set(initialState),
  resetFilters: () =>
    set({
      currentMonth: getCurrentMonth(),
      searchTerm: '',
      sectionType: null,
    }),
}));
